import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import {
  Lock, ArrowRight, CheckCircle, Lightning, Envelope,
} from "@phosphor-icons/react";

const BENEFITS = [
  "Unbegrenzte Rezepte & Zutaten",
  "Alle deine Daten bleiben erhalten",
  "PDF / Excel / Label-Export",
  "Eigenes Firmenlogo in Exports",
  "E-Mail-Support < 24 h",
];

export default function TrialExpired() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col" data-testid="trial-expired-screen">
      <header className="bg-white border-b border-zinc-200 px-5 py-4 flex items-center justify-between">
        <Link to="/" className="font-heading font-bold text-base flex items-center gap-2">
          <div className="w-7 h-7 rounded-sm bg-[#002FA7] flex items-center justify-center">
            <Lock size={14} weight="bold" color="white" />
          </div>
          NutriCalc
        </Link>
        <Button variant="ghost" onClick={logout} className="rounded-sm text-xs"
          data-testid="trial-expired-logout">
          Abmelden
        </Button>
      </header>

      <main className="flex-1 flex items-center justify-center p-5 sm:p-10">
        <div className="max-w-3xl w-full">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-100 border border-amber-300 text-amber-900 text-[11px] uppercase tracking-widest rounded-full mb-5">
            <Lightning size={12} weight="fill" /> Testphase beendet
          </div>
          <h1 className="font-heading text-3xl sm:text-5xl font-extrabold tracking-tight leading-tight">
            Deine 7 Tage sind vorbei.
            <br />
            <span className="text-[#002FA7]">Schließe jetzt deinen Plan ab</span>
            <br />
            und arbeite weiter wie bisher.
          </h1>
          <p className="mt-4 text-base text-zinc-600 max-w-2xl">
            Hi {user?.full_name?.split(" ")[0] || "dort"}, deine kostenlose Testphase
            für „<strong>{user?.company_name}</strong>" ist abgelaufen.
            Deine Daten sind sicher gespeichert — aktiviere deinen Account und du hast sofort
            wieder vollen Zugriff.
          </p>

          <div className="mt-8 grid sm:grid-cols-2 gap-4 max-w-3xl">
            {/* Plan-Box */}
            <div className="bg-white border-2 border-[#002FA7] rounded-sm p-6 shadow-lg">
              <div className="text-xs uppercase tracking-widest text-[#002FA7] font-bold mb-1">Team-Plan</div>
              <div className="font-heading text-2xl font-bold tracking-tight">Volle Power.</div>
              <div className="my-4 font-heading text-4xl font-extrabold tracking-tight leading-none">
                149 € <span className="text-base font-normal text-zinc-500">/ Monat</span>
              </div>
              <div className="text-[11px] text-zinc-500 mb-4">5 Nutzer · Jederzeit kündbar · §19 UStG (keine MwSt.)</div>
              <ul className="space-y-2 text-sm mb-5">
                {BENEFITS.map((b) => (
                  <li key={b} className="flex items-start gap-2">
                    <CheckCircle size={16} weight="fill" className="text-[#002FA7] mt-0.5 flex-shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <a href={`mailto:vahits@icloud.com?subject=NutriCalc%20Plan%20aktivieren%20-%20${encodeURIComponent(user?.company_name || "")}&body=${encodeURIComponent(
                `Hallo Vahit,\n\nich möchte den Team-Plan für meine Firma „${user?.company_name}" aktivieren.\n\nMeine Daten:\n- Firma: ${user?.company_name}\n- E-Mail: ${user?.email}\n- Name: ${user?.full_name}\n\nViele Grüße`
              )}`}
                 className="block">
                <Button className="w-full bg-[#002FA7] hover:bg-[#00248a] rounded-sm h-11 text-sm font-medium"
                  data-testid="trial-expired-upgrade">
                  <Envelope size={16} weight="bold" className="mr-2" />
                  Plan jetzt aktivieren
                </Button>
              </a>
            </div>

            {/* Info-Box */}
            <div className="space-y-4">
              <div className="bg-white border border-zinc-200 rounded-sm p-5">
                <h3 className="font-bold text-sm mb-2 flex items-center gap-2">
                  <CheckCircle size={16} weight="fill" className="text-emerald-600" />
                  Deine Daten sind sicher
                </h3>
                <p className="text-xs text-zinc-600 leading-relaxed">
                  Alle Rezepte, Zutaten und Einstellungen bleiben vollständig erhalten.
                  Nach Aktivierung deines Plans kannst du sofort weiterarbeiten — kein
                  Datenverlust, kein Re-Setup.
                </p>
              </div>
              <div className="bg-white border border-zinc-200 rounded-sm p-5">
                <h3 className="font-bold text-sm mb-2 flex items-center gap-2">
                  <Envelope size={16} weight="duotone" className="text-[#002FA7]" />
                  Lieber persönlich?
                </h3>
                <p className="text-xs text-zinc-600 leading-relaxed mb-3">
                  Schreib direkt eine Mail an{" "}
                  <a href="mailto:vahits@icloud.com" className="text-[#002FA7] underline">
                    vahits@icloud.com
                  </a>
                  . Du bekommst die Rechnung und Zugangsdaten innerhalb von 24 Stunden.
                </p>
                <p className="text-xs text-zinc-600 leading-relaxed">
                  Brauchst du mehr Zeit zum Testen? Sag Bescheid — wir verlängern gerne unbürokratisch.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-3 text-xs text-zinc-500">
            <span>Bei Fragen: <a href="mailto:vahits@icloud.com" className="text-[#002FA7] hover:underline">vahits@icloud.com</a></span>
            <span>·</span>
            <Link to="/agb" className="hover:text-zinc-800">AGB</Link>
            <span>·</span>
            <Link to="/datenschutz" className="hover:text-zinc-800">Datenschutz</Link>
          </div>
        </div>
      </main>
    </div>
  );
}
